import { IngestJobPhase } from "@nendaTypes/NendaTypes";

/**
 * The phases in which an ingest job is still moving towards delivery. A job in one of these is
 * being worked on by somebody and the portal keeps watching it.
 */
export const ACTIVE_INGEST_PHASES: IngestJobPhase[] = [
  "queued",
  "transcoding",
  "assembly",
  "ingesting",
  "publishing",
];

export function isActiveIngestPhase(phase?: string): boolean {
  return ACTIVE_INGEST_PHASES.includes(phase as IngestJobPhase);
}

/** The phases a failed job can be said to have failed in, and be retried towards. */
export type FailedIngestPhase =
  | "queued"
  | "transcoding"
  | "assembly"
  | "ingesting"
  | "publishing";

/**
 * The phase the last `phaseLog` entry implies the job then failed in.
 *
 * This is a lookup and not "the next entry in `IngestJobPhase` order" on purpose. A job whose
 * log ends at `transcoding` and that carries no assembly evidence was produced by a pipeline in
 * which `transcoding` was followed directly by `ingesting`, so walking the current phase order
 * would attribute its failure to a phase it never entered.
 */
const FAILED_PHASE_BY_LAST_LOGGED: Record<string, FailedIngestPhase> = {
  queued: "transcoding",
  transcoding: "ingesting",
  assembly: "ingesting",
  ingesting: "publishing",
};

/** Incident type the orchestrator records immediately before it fails a job on a timeout. */
const PHASE_TIMEOUT = "phase_timeout";

export interface FailedPhaseInput {
  phaseLog: { phase: string }[];
  incidents?: { type: string; phase?: string }[];
}

/**
 * Which phase a failed ingest job failed in.
 *
 * Reads two signals, in order.
 *
 * 1. The `phase` carried by the most recent `phase_timeout` incident. The orchestrator records
 *    that incident and then sets `currentPhase.phase` to `failed` in place, pushing nothing onto
 *    `phaseLog`, so the incident is where the failing phase is named.
 * 2. Otherwise the phase implied by the last `phaseLog` entry — the inference that existed
 *    before the incident was read, kept for jobs that failed without a timeout and for jobs
 *    whose incidents were never recorded.
 *
 * An empty `phaseLog` means the job never left `queued`.
 */
export function resolveFailedPhase(job: FailedPhaseInput): FailedIngestPhase {
  const timedOutPhase = [...(job.incidents ?? [])]
    .reverse()
    .find(
      (incident) =>
        incident.type === PHASE_TIMEOUT &&
        !!incident.phase &&
        incident.phase in FAILED_PHASE_BY_LAST_LOGGED
    )?.phase;

  if (timedOutPhase) return timedOutPhase as FailedIngestPhase;

  const lastLogged = job.phaseLog[job.phaseLog.length - 1]?.phase;
  if (!lastLogged) return "queued";

  return FAILED_PHASE_BY_LAST_LOGGED[lastLogged] ?? "queued";
}

/**
 * The retry route for a failed job: which action the portal asks for, given the phase it failed
 * in.
 *
 * `assembly` routes to `ingesting`, which is the orchestrator's `retryIngesting` endpoint. That
 * endpoint decides what to do from where the asset's files actually are rather than from the
 * phase name, and it accepts a `phase_timeout` recorded on `assembly` as well as on `ingesting`.
 * The alternative — routing an assembly failure to `queued` — re-transcodes an asset whose
 * renditions may be intact, which is the action that lost three assets on 2026-09-14.
 */
export function retryRouteForFailedPhase(
  failedPhase: FailedIngestPhase
): "queued" | "ingesting" | "publishing" {
  switch (failedPhase) {
    case "queued":
    case "transcoding":
      return "queued";
    case "assembly":
    case "ingesting":
      return "ingesting";
    case "publishing":
      return "publishing";
  }
}
